import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import EditableSection from '../../../components/EditableSection';
import CustomButton from '../../../components/CustomButton';
import { tableCellClass, tableHeaderCellClass } from '../../../components/TableCard';
import useEditableDraft from '../../../hooks/useEditableDraft';
import Permission from '../../../common/permissions';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import {
    useGetGrantWindowsQuery,
    usePreviewGrantWindowsMutation,
    useSaveGrantWindowsMutation,
} from '../../../api/organization/organizationApiSlice';
import type { GrantWindowsPreview } from '../../../api/organization/allocationWindowTypes';
import {
    WEEKDAYS,
    WindowDraftRow,
    draftEquals,
    formatDay,
    newRow,
    rowProblem,
    toDraft,
    toInput,
    windowErrorMessage,
} from './windowDraft';

export const WEEKDAY_KEYS: Record<number, string> = {
    1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat', 7: 'sun',
};

const inputClass = 'rounded-md border border-foreground/15 px-2 py-1.5 text-sm bg-text-contrast text-foreground '
    + 'focus:outline-none focus:border-foreground/40';

interface GrantWindowsEditorProps {
    organizationId: string;
    grantId: string;
    /** The heading: the room on the organisation's page, the organisation on the room's. */
    title: string;
    /** Prefills a new row's validity: the tenant's series season, when set. */
    season: { from: string, to: string } | null;
}

let rowSeq = 0;
const nextKey = () => {
    rowSeq += 1;
    return `new-${rowSeq}`;
};

/**
 * An organisation's allocation windows on one shared room (ADR-0027, M4 spec
 * §6): the weekly times the room is theirs, each valid for a season.
 *
 * View first, then "Bearbeiten" (design-decisions › Bearbeiten und Speichern).
 * "Speichern" first asks the server what the save would do (the preview):
 * - an overlap with another organisation's window blocks it — shown, never saved;
 * - bookings by others inside the new windows (D2), released dates the new
 *   windows no longer cover (#3510) and released dates that would free more
 *   time than was released because the window grew or moved (lead-dev review
 *   §4.1) are listed and need a second, explicit confirmation. Bookings are
 *   never removed: they stay and are listed.
 *
 * One component on both sides — the organisation's rooms and the room's
 * organisations — so the two cannot drift.
 */
const GrantWindowsEditor: React.FC<GrantWindowsEditorProps> = ({ organizationId, grantId, title, season }) => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';
    const canWrite = useSelector(makeHasPermissionSelector(Permission.writeCustomers()));
    const { data, isLoading, isError } = useGetGrantWindowsQuery({ organizationId, grantId });
    const [preview, { isLoading: previewing }] = usePreviewGrantWindowsMutation();
    const [save, { isLoading: saving }] = useSaveGrantWindowsMutation();

    const saved = React.useMemo(() => toDraft(data?.windows ?? []), [data]);
    const draft = useEditableDraft<WindowDraftRow[]>(saved, draftEquals);
    const [error, setError] = useState<string | null>(null);
    // The preview that asked for confirmation, for exactly the draft it was made for.
    const [pending, setPending] = useState<{ preview: GrantWindowsPreview, draft: WindowDraftRow[] } | null>(null);
    const [showProblems, setShowProblems] = useState(false);

    const rows = draft.value;
    const problems = rows.map(rowProblem);
    const valid = problems.every((problem) => problem === null);
    const confirmable = pending !== null && draftEquals(pending.draft, rows);

    const update = (key: string, change: Partial<WindowDraftRow>) => {
        draft.setDraft(rows.map((row) => (row.key === key ? { ...row, ...change } : row)));
    };

    const close = () => {
        setPending(null);
        setError(null);
        setShowProblems(false);
    };

    const write = async (dropReleasedDates: boolean) => {
        try {
            await save({ organizationId, grantId, windows: toInput(rows), dropReleasedDates }).unwrap();
            close();
            draft.finish();
        } catch (err) {
            setPending(null);
            setError(windowErrorMessage(err, t('allocation_windows.editor.save_error')));
        }
    };

    const onSave = async () => {
        setError(null);
        if (!valid) {
            setShowProblems(true);
            return;
        }
        try {
            const result = await preview({ organizationId, grantId, windows: toInput(rows) }).unwrap();
            if (result.overlaps.length > 0) {
                setPending({ preview: result, draft: rows });
                return;
            }
            const needsConfirmation = result.bookingConflicts.length > 0
                || result.droppedReleases.length > 0
                || (result.widenedReleases ?? []).length > 0;
            if (needsConfirmation) {
                setPending({ preview: result, draft: rows });
                return;
            }
            await write(false);
        } catch (err) {
            setError(windowErrorMessage(err, t('allocation_windows.editor.save_error')));
        }
    };

    const timeOf = (row: { weekday: number, startTime: string, endTime: string }) => t('allocation_windows.time', {
        weekday: t(`asset_detail.weekdays.${WEEKDAY_KEYS[row.weekday]}`),
        start: row.startTime,
        end: row.endTime,
    });
    const validity = (row: { validFrom: string, validTo: string }) =>
        `${formatDay(row.validFrom, language)} – ${formatDay(row.validTo, language)}`;

    if (isLoading) {
        return <p className={'text-sm text-foreground/60'}>{t('common.loading')}</p>;
    }
    // A failed read is not an empty list (CLAUDE.md, A Failure Must Never Look Like an Absence).
    if (isError || !data) {
        return <p className={'text-sm text-foreground/60'}>{t('allocation_windows.editor.load_error')}</p>;
    }

    const view = data.windows.length === 0 ? (
        <p className={'text-sm text-foreground/60'}>{t('allocation_windows.editor.empty')}</p>
    ) : (
        <div className={'overflow-x-auto'}>
            <table className={'w-full text-sm'}>
                <thead>
                    <tr className={'border-b border-foreground/5'}>
                        <th className={tableHeaderCellClass}>{t('allocation_windows.columns.time')}</th>
                        <th className={tableHeaderCellClass}>{t('allocation_windows.columns.validity')}</th>
                        <th className={tableHeaderCellClass}>{t('allocation_windows.columns.released')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.windows.map((window) => (
                        <tr key={window.id} className={'border-b border-foreground/5 last:border-b-0'}>
                            <td className={tableCellClass}>{timeOf(window)}</td>
                            <td className={tableCellClass}>{validity(window)}</td>
                            <td className={tableCellClass}>
                                {window.releaseDates.length === 0
                                    ? t('allocation_windows.none_released')
                                    : window.releaseDates.map((day) => formatDay(day, language)).join(', ')}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const shown = confirmable ? pending?.preview : null;

    return (
        <EditableSection
            title={title}
            description={t('allocation_windows.editor.description')}
            editing={draft.editing}
            readOnly={!canWrite}
            isDirty={draft.isDirty}
            saving={previewing || saving}
            error={error}
            onEdit={() => {
                close();
                draft.startEdit();
            }}
            onCancel={() => {
                close();
                draft.cancel();
            }}
            onSave={onSave}
            view={view}
        >
            <div className={'flex flex-col gap-3'}>
                {rows.length === 0 && (
                    <p className={'text-sm text-foreground/60'}>{t('allocation_windows.editor.empty_draft')}</p>
                )}
                {rows.map((row, index) => (
                    <div key={row.key} className={'flex flex-col gap-1'}>
                        <div className={'flex flex-wrap items-end gap-2'}>
                            <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                                {t('allocation_windows.fields.weekday')}
                                <select
                                    className={inputClass}
                                    value={row.weekday}
                                    onChange={(event) => update(row.key, { weekday: Number(event.target.value) })}
                                >
                                    {WEEKDAYS.map((weekday) => (
                                        <option key={weekday} value={weekday}>
                                            {t(`asset_detail.weekdays.${WEEKDAY_KEYS[weekday]}`)}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                                {t('allocation_windows.fields.start')}
                                <input
                                    type={'time'}
                                    className={inputClass}
                                    value={row.startTime}
                                    onChange={(event) => update(row.key, { startTime: event.target.value })}
                                />
                            </label>
                            <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                                {t('allocation_windows.fields.end')}
                                <input
                                    type={'time'}
                                    className={inputClass}
                                    value={row.endTime === '24:00' ? '00:00' : row.endTime}
                                    // A time input cannot say 24:00; midnight as an END means the end of the day.
                                    onChange={(event) => update(row.key, {
                                        endTime: event.target.value === '00:00' ? '24:00' : event.target.value,
                                    })}
                                />
                            </label>
                            <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                                {t('allocation_windows.fields.valid_from')}
                                <input
                                    type={'date'}
                                    className={inputClass}
                                    value={row.validFrom}
                                    onChange={(event) => update(row.key, { validFrom: event.target.value })}
                                />
                            </label>
                            <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                                {t('allocation_windows.fields.valid_to')}
                                <input
                                    type={'date'}
                                    className={inputClass}
                                    value={row.validTo}
                                    onChange={(event) => update(row.key, { validTo: event.target.value })}
                                />
                            </label>
                            <button
                                type={'button'}
                                className={'text-sm text-primary hover:underline pb-2'}
                                onClick={() => draft.setDraft(rows.filter((candidate) => candidate.key !== row.key))}
                            >
                                {t('allocation_windows.editor.remove')}
                            </button>
                        </div>
                        {showProblems && problems[index] && (
                            <p className={'text-xs text-foreground'}>
                                {t(`allocation_windows.problem.${problems[index]}`)}
                            </p>
                        )}
                    </div>
                ))}
                <div>
                    <CustomButton
                        title={t('allocation_windows.editor.add')}
                        isFilled={false}
                        onClick={() => draft.setDraft([...rows, newRow(nextKey(), season)])}
                    />
                </div>

                {shown && shown.overlaps.length > 0 && (
                    <div role={'alert'} className={'rounded-[5px] border border-foreground/10 px-4 py-3 text-sm'}>
                        <p className={'font-medium text-foreground'}>{t('allocation_windows.editor.overlap_title')}</p>
                        <ul className={'list-disc pl-5 text-foreground/80'}>
                            {shown.overlaps.map((overlap) => (
                                <li key={overlap.windowId}>
                                    {t('allocation_windows.editor.overlap_line', {
                                        name: overlap.organizationName || t('common.not_applicable'),
                                        time: timeOf(overlap),
                                        validity: validity(overlap),
                                    })}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {shown && shown.overlaps.length === 0 && (
                    <div role={'alert'} className={'flex flex-col gap-3 rounded-[5px] border border-foreground/10 px-4 py-3 text-sm'}>
                        {shown.bookingConflicts.length > 0 && (
                            <div>
                                <p className={'font-medium text-foreground'}>
                                    {t('allocation_windows.editor.conflicts_title', { total: shown.bookingConflicts.length })}
                                </p>
                                <p className={'text-foreground/60'}>{t('allocation_windows.editor.conflicts_hint')}</p>
                                <ul className={'list-disc pl-5 text-foreground/80'}>
                                    {shown.bookingConflicts.map((conflict) => (
                                        <li key={`${conflict.orderId}-${conflict.date}`}>
                                            {t('allocation_windows.editor.conflict_line', {
                                                number: conflict.bookingNumber || t('common.not_applicable'),
                                                date: formatDay(conflict.date, language),
                                                room: conflict.groupName || t('common.not_applicable'),
                                            })}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {shown.droppedReleases.length > 0 && (
                            <div>
                                <p className={'font-medium text-foreground'}>{t('allocation_windows.editor.dropped_title')}</p>
                                <p className={'text-foreground/80'}>
                                    {shown.droppedReleases
                                        .flatMap((dropped) => dropped.releaseDates)
                                        .map((day) => formatDay(day, language))
                                        .join(', ')}
                                </p>
                            </div>
                        )}
                        {(shown.widenedReleases ?? []).length > 0 && (
                            <div>
                                <p className={'font-medium text-foreground'}>{t('allocation_windows.editor.widened_title')}</p>
                                <p className={'text-foreground/60'}>{t('allocation_windows.editor.widened_hint')}</p>
                                <ul className={'list-disc pl-5 text-foreground/80'}>
                                    {(shown.widenedReleases ?? []).map((release) => (
                                        <li key={`${release.date}-${release.fromStartTime}-${release.toStartTime}-${release.toEndTime}`}>
                                            {t('allocation_windows.editor.widened_line', {
                                                date: formatDay(release.date, language),
                                                from: `${release.fromStartTime}–${release.fromEndTime}`,
                                                to: `${release.toStartTime}–${release.toEndTime}`,
                                            })}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <div>
                            <CustomButton
                                title={t('allocation_windows.editor.confirm_save')}
                                loading={saving}
                                onClick={() => write(shown.droppedReleases.length > 0)}
                            />
                        </div>
                    </div>
                )}
            </div>
        </EditableSection>
    );
};

export default GrantWindowsEditor;
